/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * UI: Session Evicted Modal.
 */

import React, { useEffect, useState } from "react";

/**
 * Explains why the user is about to be signed out, then hands off to the real Auth0 logout.
 *
 * The delay is the point: an eviction is involuntary and surprising, so the user needs a moment to
 * read what happened before the browser leaves for Auth0.
 */

const COUNTDOWN_SECONDS = 20;

const CONTENT = {
  SESSION_SUPERSEDED: {
    title: "Signed in on another device",
    body:
      "Your FareLanes account was just used to sign in somewhere else. Your plan allows one active device at a time, so this session is being closed.",
    hint: "If this wasn't you, sign back in and change your password.",
    accent: "#d9822b",
  },
  SESSION_EXPIRED: {
    title: "Your session has expired",
    body: "You've been signed out because this session was inactive for too long.",
    hint: "Signing back in will pick up right where you left off.",
    accent: "#4a6fa5",
  },
  SESSION_REQUIRED: {
    title: "We couldn't verify this session",
    body: "This browser's session could not be confirmed, so you're being signed out as a precaution.",
    hint: "Signing back in will fix this.",
    accent: "#4a6fa5",
  },
};

export const SessionEvictedModal = ({ reason, onSignOut }) => {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [barWidth, setBarWidth] = useState(100);

  const content = CONTENT[reason] || CONTENT.SESSION_EXPIRED;

  useEffect(() => {
    const signOutTimer = setTimeout(onSignOut, COUNTDOWN_SECONDS * 1000);
    const tick = setInterval(() => {
      setSecondsLeft((remaining) => (remaining > 0 ? remaining - 1 : 0));
    }, 1000);

    // Kick the bar to 0 on the next frame so the CSS transition has a starting value to move from.
    const frame = requestAnimationFrame(() => setBarWidth(0));

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      clearTimeout(signOutTimer);
      clearInterval(tick);
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
    };
  }, [onSignOut]);

  return (
    <div style={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="session-evicted-title">
      <div style={styles.card}>
        <div style={{ ...styles.iconRing, borderColor: content.accent }}>
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke={content.accent} strokeWidth="2" />
            <path d="M12 7v6" stroke={content.accent} strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="16.5" r="1.2" fill={content.accent} />
          </svg>
        </div>

        <h2 id="session-evicted-title" style={styles.title}>
          {content.title}
        </h2>
        <p style={styles.body}>{content.body}</p>
        <p style={styles.hint}>{content.hint}</p>

        <div style={styles.progressTrack}>
          <div
            style={{
              ...styles.progressFill,
              backgroundColor: content.accent,
              width: `${barWidth}%`,
              transition: `width ${COUNTDOWN_SECONDS}s linear`,
            }}
          />
        </div>

        <p style={styles.countdown}>
          {secondsLeft > 0
            ? `Redirecting to sign in in ${secondsLeft} second${secondsLeft === 1 ? "" : "s"}...`
            : "Redirecting to sign in..."}
        </p>

        <button type="button" style={{ ...styles.button, backgroundColor: content.accent }} onClick={onSignOut}>
          Sign in again now
        </button>
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 2147483647,
    backgroundColor: "rgba(17, 24, 39, 0.72)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.35)",
    maxWidth: "460px",
    width: "100%",
    padding: "36px 32px 28px",
    textAlign: "center",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  iconRing: {
    width: "64px",
    height: "64px",
    margin: "0 auto 20px",
    borderRadius: "50%",
    border: "2px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    margin: "0 0 12px",
    fontSize: "21px",
    fontWeight: 600,
    color: "#111827",
  },
  body: {
    margin: "0 0 10px",
    fontSize: "15px",
    lineHeight: 1.55,
    color: "#4b5563",
  },
  hint: {
    margin: "0 0 24px",
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#9ca3af",
  },
  progressTrack: {
    height: "4px",
    width: "100%",
    borderRadius: "2px",
    backgroundColor: "#e5e7eb",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: "2px",
  },
  countdown: {
    margin: "12px 0 22px",
    fontSize: "13px",
    color: "#6b7280",
  },
  button: {
    border: "none",
    borderRadius: "8px",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: 600,
    padding: "11px 26px",
    width: "100%",
  },
};
